import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@apollo/client';
import './MyAccount.scss';
import { MembershipTable } from './components/MembershipTable';
import { ExtensionEnterprise } from './components/ExtensionEnterprice';
import { useAuth, useApp, useError } from '../../contexts';
import {
  GET_MEMBERSHIP,
  GET_USER_INFO,
  USER_ORDERS,
  IS_ENTERPRISE_EXTENSION_ALLOWED,
} from '../../services/ApolloClient';
import { BillingHistory } from './components/BillingHistory';
import { EcomOrders } from './components/Orders';
import { EMembershipPlatform, EMembershipStatus } from './components/types';
import { PaymentMethod } from './components/PaymentMethod';
import { UserAccountSection } from './components/UserAccountSection';
import { UpdatePaymentMethodFlex } from './components/UpdatePaymentMethodFlex';
import { formatTimestampDateString } from '../../utils';

function MyAccount() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isCancelled = searchParams.get('cancellation');
  const token = searchParams.get('token');
  const { showError } = useError();

  const { data: userData, loading: userDataLoading } = useQuery(GET_USER_INFO, {
    fetchPolicy: 'no-cache',
  });
  const {
    data: membershipData,
    loading: membershipDataLoading,
    refetch: membershipRefetch,
  } = useQuery(GET_MEMBERSHIP, {
    fetchPolicy: 'no-cache',
    variables: {
      forceUpdate: true,
    },
  });

  const { data: isEligibleToExtend } = useQuery(IS_ENTERPRISE_EXTENSION_ALLOWED, {
    fetchPolicy: 'no-cache',
  });

  const { data: orderData, loading: orderDataLoading } = useQuery(USER_ORDERS, {
    fetchPolicy: 'no-cache',
  });
  const { login } = useAuth();
  const { setLoading } = useApp();

  const [isMembershipRefetchLoading, setIsMembershipRefetchLoading] = useState(false);

  useEffect(() => {
    if (token) {
      login(token);
    }
  }, [login, token]);

  useEffect(() => {
    setLoading(
      userDataLoading || membershipDataLoading || orderDataLoading || isMembershipRefetchLoading,
    );
  }, [
    isMembershipRefetchLoading,
    userDataLoading,
    membershipDataLoading,
    orderDataLoading,
    setLoading,
  ]);

  useEffect(() => {
    let limitRequests = 5;
    const fetchData = async () => {
      try {
        const refetchData = await membershipRefetch();
        if (
          refetchData?.data?.membership?.status === EMembershipStatus.cancelled ||
          !limitRequests
        ) {
          setIsMembershipRefetchLoading(false);
          setSearchParams([]);
        } else {
          limitRequests -= 1;
          fetchData();
        }
      } catch {
        showError();
      }
    };

    if (isCancelled) {
      setIsMembershipRefetchLoading(true);
      fetchData();
    }
  }, [isCancelled, membershipRefetch, setSearchParams, showError]);

  const getPaymentFailedMessage = () => {
    if (!membershipData || !membershipData.membership) {
      return null;
    }
    if (membershipData?.membership.status === EMembershipStatus.past_due) {
      if (membershipData?.membership.updatedPaymentMethodAt) {
        return (
          <div style={{ marginTop: '10px' }}>
            <b>Your payment is being processed.</b>
          </div>
        );
      }
      if (membershipData?.membership?.gracePeriodExpiresDate) {
        return (
          <div style={{ color: 'red', marginTop: '10px' }}>
            <b>Payment failed.</b> Please update your payment method by&nbsp;
            {formatTimestampDateString(membershipData?.membership?.gracePeriodExpiresDate)} to keep
            your membership active.
          </div>
        );
      }
      return (
        <div style={{ color: 'red', marginTop: '10px' }}>
          <b>Payment failed.</b> Please update your payment method to keep your membership active.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <Helmet>
        <title>Account | Every Mother</title>
      </Helmet>
      {!userDataLoading && !membershipDataLoading && !orderDataLoading && (
        <div className="account-container">
          {membershipData && (
            <div className="membership-container">
              <h3>My Memberships</h3>
              <MembershipTable data={membershipData.membership} />
              {isEligibleToExtend?.isEnterpriseExtensionAllowed === true && (
                <ExtensionEnterprise data={membershipData.membership} />
              )}
              <PaymentMethod data={membershipData.membership} />
              <BillingHistory />
              {getPaymentFailedMessage()}
              {membershipData?.membership?.platform === EMembershipPlatform.flex &&
                membershipData?.membership?.status !== EMembershipStatus.cancelled &&
                membershipData?.membership?.status !== EMembershipStatus.expired && (
                  <UpdatePaymentMethodFlex />
                )}
            </div>
          )}
          {orderData?.userOrders && <EcomOrders orders={orderData?.userOrders} />}
          {userData?.me && (
            <UserAccountSection
              userData={userData?.me}
              membershipData={membershipData?.membership}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default MyAccount;
